import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { FaRegTrashAlt } from 'react-icons/fa';
import { FaPencil } from 'react-icons/fa6';
import { RiPushpinFill } from 'react-icons/ri';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import {
  richHtmlSx,
  RichTextEditor,
  type TiptapDoc,
  tiptapDocToHtml,
  useConfirmService,
  useContextMenuService,
  useMentionResolver,
} from '@onesource/components';
import type { TCommentWithAuthor, TPostWithAuthor } from '@onesource/schemas';
import {
  fetchMentionUsersByIds,
  fetchMentionUsers,
  getMediaUrl,
  getSession,
  useDeletePost,
  useGetAllProfileQuery,
  useGetComments,
  useUpdatePost,
} from '@onesource/services';
import { useNavigate } from '@tanstack/react-router';
import { formatDistanceToNow } from 'date-fns';
import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useState, type MouseEvent } from 'react';
import { CommentInput } from './comment-input';
import { CommentItem } from './comment-item';
import { ReactionPicker } from './reaction-picker';
import { ReactorsAvatarStack } from './reactors-avatar-stack';

interface PostCardProps {
  post: TPostWithAuthor;
  spaceId: string;
  variant?: 'feed' | 'detail';
  onAfterDelete?: () => void;
}

const MAX_BODY_LENGTH = 300;

export const PostCard = ({ post, spaceId, variant = 'feed', onAfterDelete }: PostCardProps) => {
  const navigate = useNavigate();
  const isDetail = variant === 'detail';
  const [showFullBody, setShowFullBody] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const theme = useTheme();
  const { setContextMenu } = useContextMenuService();
  const { setConfirm } = useConfirmService();

  const token = getSession();
  const { data: profile } = useGetAllProfileQuery({ token });
  const { data: comments } = useGetComments(post.id);
  const deletePost = useDeletePost(post.id);
  const updatePost = useUpdatePost(post.id);

  const isAuthor = profile?.authUserId === post.author.id;

  const handleEditClose = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleDelete = useCallback(() => {
    setConfirm({
      title: 'Delete',
      open: true,
      confirmBtnLabel: 'Remove',
      cancelBtnLabel: 'Cancel',
      message: 'This will permanently remove your post. Are you sure you want to continue?',
      onConfirm: async () => {
        await deletePost.mutateAsync();
        onAfterDelete?.();
      },
    });
  }, [setConfirm, deletePost, onAfterDelete]);

  const handleMenuClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      setContextMenu({
        anchorEl: event.currentTarget,
        items: [
          {
            id: 'edit',
            label: 'Edit',
            icon: <FaPencil color={theme.palette.primary.main} size={16} />,
            color: 'info',
            context: post,
            onClick: handleEdit,
          },
          {
            id: 'delete',
            label: 'Delete',
            icon: <FaRegTrashAlt color={theme.palette.primary.main} size={16} />,
            color: 'info',
            context: post,
            onClick: handleDelete,
          },
        ],
      });
    },
    [setContextMenu, theme.palette.primary.main, post, handleEdit, handleDelete],
  );

  const handleBodyClick = (event: React.MouseEvent<HTMLElement>) => {
    if (isDetail) {
      return;
    }
    if ((event.target as HTMLElement).closest('a')) {
      return;
    }
    navigate({
      to: '/community-hub/spaces/$id/posts/$postId',
      params: { id: spaceId, postId: post.id },
    });
  };

  const handleBodyKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (isDetail) {
      return;
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    navigate({
      to: '/community-hub/spaces/$id/posts/$postId',
      params: { id: spaceId, postId: post.id },
    });
  };

  // Parse Tiptap JSON body and resolve mention display names
  const bodyDoc = useMemo<TiptapDoc | null>(() => {
    try {
      return JSON.parse(post.body);
    } catch {
      return null;
    }
  }, [post.body]);
  const resolvedDoc = useMentionResolver(bodyDoc, fetchMentionUsersByIds);

  const [editBody, setEditBody] = useState<TiptapDoc | null>(bodyDoc);

  useEffect(() => {
    if (isEditing) {
      setEditBody(bodyDoc);
    }
  }, [isEditing, bodyDoc]);

  const handleEditSave = async () => {
    if (!editBody) {
      return;
    }
    await updatePost.mutateAsync({ body: JSON.stringify(editBody) });
    setIsEditing(false);
  };

  const [resolvedBodyHtml, setResolvedBodyHtml] = useState<string>('');

  useEffect(() => {
    if (resolvedDoc) {
      setResolvedBodyHtml(DOMPurify.sanitize(tiptapDocToHtml(resolvedDoc)));
      return;
    }
    const rawHtml = post.bodyHtml ?? post.body;
    const uuidRegex = /@([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/gi;
    const uuids = [...new Set([...rawHtml.matchAll(uuidRegex)].map(m => m[1]))];
    if (uuids.length === 0) {
      setResolvedBodyHtml(DOMPurify.sanitize(rawHtml));
      return;
    }
    fetchMentionUsersByIds(uuids).then(users => {
      let resolved = rawHtml;
      for (const user of users) {
        resolved = resolved.replaceAll(`@${user.id}`, `<span class="mention">@${user.displayName}</span>`);
      }
      setResolvedBodyHtml(DOMPurify.sanitize(resolved));
    });
  }, [resolvedDoc, post.bodyHtml, post.body]);

  const bodyHtml = resolvedBodyHtml || DOMPurify.sanitize(post.bodyHtml ?? post.body);

  const timeAgo = formatDistanceToNow(new Date(post.createdAt), { addSuffix: false });

  return (
    <Box
      sx={{
        bgcolor: 'white',
        borderRadius: '15px',
        p: '15px',
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
      }}
    >
      {/* Post Header - Author Info */}
      <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <Avatar
          src={getMediaUrl(post.author.avatar)}
          alt={post.author.name}
          sx={{ width: 30, height: 30, borderRadius: '22.5px' }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Figtree, sans-serif',
              fontWeight: 700,
              fontSize: 14,
              lineHeight: '19px',
              color: '#333',
            }}
          >
            {post.author.name}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Figtree, sans-serif',
              fontWeight: 400,
              fontSize: 9,
              lineHeight: '17px',
              color: '#808080',
            }}
          >
            {timeAgo}
          </Typography>
        </Box>
        {post.isPinned && (
          <Chip
            icon={<RiPushpinFill size={12} />}
            label="Pinned"
            size="small"
            sx={{
              height: 22,
              borderRadius: '11px',
              bgcolor: theme => `${theme.palette.primary.main}1A`,
              color: theme => theme.palette.primary.main,
              fontFamily: 'Figtree, sans-serif',
              fontSize: 12,
              fontWeight: 500,
              '& .MuiChip-icon': {
                color: theme => theme.palette.primary.main,
                ml: '8px',
                mr: '-4px',
              },
              '& .MuiChip-label': { px: '8px' },
            }}
          />
        )}
        {isAuthor && (
          <IconButton size="small" sx={{ p: 0 }} onClick={isEditing ? handleEditClose : handleMenuClick}>
            {isEditing ? (
              <CloseIcon sx={{ fontSize: 18, color: '#333' }} />
            ) : (
              <MoreHorizIcon sx={{ fontSize: 18, color: '#333' }} />
            )}
          </IconButton>
        )}
      </Box>

      {/* Post Content */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
        {isEditing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <RichTextEditor
              value={editBody}
              onChange={setEditBody}
              onFetchMentionUsers={fetchMentionUsers}
              placeholder="Edit your post..."
            />
            <Box sx={{ ml: 'auto' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleEditSave}
                disabled={!editBody || updatePost.isPending}
                startIcon={updatePost.isPending ? <CircularProgress size={14} color="inherit" /> : undefined}
                sx={{
                  borderRadius: '20px',
                  textTransform: 'none',
                  fontFamily: 'Figtree, sans-serif',
                  fontWeight: 600,
                  minHeight: 30,
                  px: '12px',
                }}
              >
                {updatePost.isPending ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          bodyHtml && (
            <Box
              dangerouslySetInnerHTML={{ __html: bodyHtml }}
              onClick={handleBodyClick}
              onKeyDown={handleBodyKeyDown}
              role={isDetail ? undefined : 'button'}
              tabIndex={isDetail ? undefined : 0}
              aria-label={isDetail ? undefined : 'Open post'}
              sx={{
                ...richHtmlSx,
                wordBreak: 'break-word',
                fontFamily: 'Figtree, sans-serif',
                fontWeight: 400,
                fontSize: 14,
                lineHeight: '19px',
                color: '#333',
                cursor: isDetail ? 'default' : 'pointer',
                ...(!isDetail &&
                  !showFullBody && {
                    display: '-webkit-box',
                    WebkitLineClamp: 5,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }),
                '& span.mention': {
                  color: theme => theme.palette.primary.main,
                  fontWeight: 600,
                  backgroundColor: theme => `${theme.palette.primary.main}22`,
                  borderRadius: '4px',
                  px: '4px',
                  py: '2px',
                },
              }}
            />
          )
        )}
        {!isEditing && !isDetail && !showFullBody && post.body.length > MAX_BODY_LENGTH && (
          <Typography
            onClick={event => {
              event.stopPropagation();
              setShowFullBody(true);
            }}
            sx={{
              fontFamily: 'Figtree, sans-serif',
              fontWeight: 700,
              fontSize: 12,
              lineHeight: '17px',
              color: theme => theme.palette.primary.main,
              cursor: 'pointer',
            }}
          >
            See more
          </Typography>
        )}

        {/* Post Image Attachments */}
        {post.attachments && post.attachments.length > 0 && (
          <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap', mt: 1 }}>
            {post.attachments.map((attachment, index) => (
              <Box
                key={attachment.url}
                component="img"
                src={attachment.url}
                alt={`Attachment ${index + 1}`}
                sx={{
                  maxWidth: '100%',
                  maxHeight: isDetail ? 400 : 300,
                  borderRadius: '12px',
                  objectFit: 'cover',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  const anchor = document.createElement('a');
                  anchor.href = attachment.url;
                  anchor.target = '_blank';
                  anchor.rel = 'noopener noreferrer';
                  document.body.append(anchor);
                  anchor.click();
                  anchor.remove();
                }}
              />
            ))}
          </Box>
        )}

        {/* Post Actions */}
        <Box sx={{ display: 'flex', gap: '15px', alignItems: 'center', mt: '5px' }}>
          <Box
            sx={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => setShowComments(!showComments)}
          >
            <ChatBubbleOutlineIcon sx={{ fontSize: 12, color: '#333' }} />
            <Typography
              sx={{
                fontFamily: 'Figtree, sans-serif',
                fontWeight: 400,
                fontSize: { xs: 9, md: 9 },
                lineHeight: '17px',
                color: '#333',
              }}
            >
              Comment {post.commentCount}
            </Typography>
          </Box>

          <ReactionPicker
            targetType="post"
            targetId={post.id}
            currentReaction={post.userReaction}
            likeCount={post.likeCount}
          />

          <ReactorsAvatarStack targetType="post" targetId={post.id} likeCount={post.likeCount} />
        </Box>
      </Box>

      {/* Comments Section */}
      {(isDetail || showComments) && comments && comments.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {comments.map((comment: TCommentWithAuthor) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              postId={post.id}
              onReplyClick={() => setReplyToCommentId(comment.id)}
            />
          ))}
        </Box>
      )}

      {/* Comment Input */}
      {(isDetail || showComments || replyToCommentId) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {replyToCommentId && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Figtree, sans-serif',
                  fontSize: 12,
                  color: '#808080',
                }}
              >
                Replying to {comments?.find(c => c.id === replyToCommentId)?.author.name || 'comment'}
              </Typography>
              <Typography
                onClick={() => setReplyToCommentId(null)}
                sx={{
                  fontFamily: 'Figtree, sans-serif',
                  fontWeight: 700,
                  fontSize: 12,
                  color: '#333',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </Typography>
            </Box>
          )}
          <CommentInput
            postId={post.id}
            parentId={replyToCommentId || undefined}
            onClose={() => setReplyToCommentId(null)}
          />
        </Box>
      )}
    </Box>
  );
};
